import {
  createUndo,
  ORIGIN,
  type CollabClient,
  type CollabRoom,
  type RoomStatus,
  type Undo,
  type UndoResult,
} from '@flobi/collab-client';
import { colorFor } from '@flobi/collab-client/react';
import {
  pageGuides,
  listPages,
  pageNodes,
  readMeta,
  type LengthUnit,
  Scene,
} from '@flobi/artwork-core';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import type * as Y from 'yjs';
import { closeWhenSent } from '../live/rooms';
import { createCollabClient, myUserId, pageRoomName, rootRoomName, storedState, type Me } from '../live/collab';
import { needsAccess, refusalText, SWITCHED_OFF } from '../live/problems';

/**
 * Edits made here: one gesture, one transaction with the contract's `ORIGIN.local`. The collab
 * client sends them, and undo only takes back our own.
 */
export const LOCAL_ORIGIN = ORIGIN.local;
/**
 * A text's box written because it was measured, not because anybody changed it: synced like
 * any edit, but never an undo step of its own (undo tracks `ORIGIN.local` and nothing else).
 */
export const MEASURE_ORIGIN = 'artwork:measure';

/** Said when a press of undo finds its step taken over by someone else (CONTRACTS §21.41). */
export const UNDO_TAKEN_OVER = 'Someone else changed this since';

/**
 * The page's undo history: this person's own edits only, one gesture per step (nothing grouped
 * by time; the canvas calls stopCapturing() when a drag ends), one step per press (§21.41).
 *
 * Guides are in the scope as well as the nodes. They are not artwork, but pulling one out is still
 * a thing a person did to the file, and an undo that stepped straight over it would sometimes
 * appear to do nothing.
 *
 * When someone else has since changed what a step changed, the press consumes that step, changes
 * nothing and answers `changed-by-others` (`onTakenOver` is told, so the editor can say so). It
 * never walks on into an older step: that is what, after a collaborator moved my shape, undid its
 * creation and deleted it for everyone (QA 2026-09-24, undo-persist-collab-F2). Nor does it write
 * my old value over their later one.
 */
export function pageUndo(page: Y.Doc, onTakenOver?: () => void): Undo {
  const undo = createUndo(page, [pageNodes(page), pageGuides(page)], {
    captureTimeout: 24 * 60 * 60 * 1000,
    ignoreRemoteMapChanges: false,
  });
  const tell = (result: UndoResult): UndoResult => {
    if (!result.changed && result.reason === 'changed-by-others') onTakenOver?.();
    return result;
  };
  const undoOne = undo.undo.bind(undo);
  const redoOne = undo.redo.bind(undo);
  undo.undo = () => tell(undoOne());
  undo.redo = () => tell(redoOne());
  return undo;
}

/** How long leaving waits for unsent edits to go out before the rooms close anyway. */
const DRAIN_MS = 5_000;

export interface OpenDocument {
  /** The file this is, which uploads and exports are addressed to. */
  readonly documentId: string;
  /** The root document: the shaders, the assets and the fonts. */
  readonly root: Y.Doc;
  readonly page: Y.Doc;
  readonly scene: Scene;
  readonly undo: Undo;
  /** The page's room: edits go out through it, and presence (cursors, selections) lives on it. */
  readonly pageRoom: CollabRoom;
  readonly rootRoom: CollabRoom;
  readonly pageId: string;
  /** This person, as the others see them: their other tabs are not "someone else". */
  readonly me: Me;
}

export interface DocumentSession {
  open: OpenDocument | null;
  /** The connection, as the person needs to know it (the status pill reads it from the room). */
  status: RoomStatus;
  /** A viewer: the server refuses their edits, so the editor offers none. */
  readOnly: boolean;
  /** Edits the server has not confirmed yet. */
  pending: number;
  /** The file's name, from the root document. */
  name: string;
  /** The unit the document is thought in, and the resolution it becomes pixels at. */
  units: LengthUnit;
  resolution: number;
  /**
   * How far apart the grid is, and how many faint lines divide each square.
   *
   * The document's, like the unit: a box net drawn on a 5 mm grid is on a 5 mm grid on
   * anybody's machine. Whether the grid is SHOWN is not here — that is about the person
   * looking, and lives in the browser beside the rest of the view settings.
   */
  gridStep: number;
  gridDivisions: number;
  /** What the file is made for: the screen (RGB) or a press (CMYK). The document's. */
  colorMode: 'rgb' | 'cmyk';
  /** Null until something goes wrong that the person needs to know about. */
  problem: string | null;
  /** The room is closed for good (deleted, access removed): nothing more can be edited. */
  stopped: boolean;
  /** Refused because this person may not open it (taken off, or never on it): the access screen. */
  noAccess: boolean;
  /** Counts presses of undo/redo whose step someone else had since changed: each one is said once. */
  undoTakenOver: number;
}

/**
 * What the hook keeps to itself.
 *
 * The file format still shards a document into a root doc and one doc per page, because
 * that is what gave each its own room — but a document has exactly one page and no way to
 * make another. Nothing above this line knows pages exist; an artboard is the only thing a
 * person divides a file into.
 */
type SessionState = DocumentSession & { pageId: string | null };

const FRESH: SessionState = {
  open: null,
  status: 'connecting',
  readOnly: false,
  pending: 0,
  // Empty, not "Untitled", until the file's own name arrives (files-doc-F25).
  name: '',
  units: 'px' as LengthUnit,
  resolution: 72,
  gridStep: 72,
  gridDivisions: 8,
  colorMode: 'rgb',
  pageId: null,
  problem: null,
  stopped: false,
  noAccess: false,
  undoTakenOver: 0,
};

/**
 * What the provider counts as unsynced is 1 for a moment after every (re)connect, with nothing
 * typed: the join itself. Only a count above that is edits, until the room is synced.
 */
export function pendingOf(status: RoomStatus, unsynced: number): number {
  return status === 'synced' || unsynced > 1 ? unsynced : 0;
}

/** The connection of a file, from the rooms that make it: the page being edited speaks for it. */
export function summaryOf(root: CollabRoom, page: CollabRoom | null): Pick<SessionState, 'status' | 'readOnly' | 'pending'> {
  const speaking = page ?? root;
  return {
    status: speaking.status,
    readOnly: root.readOnly || (page?.readOnly ?? false),
    pending: pendingOf(root.status, root.unsynced) + (page ? pendingOf(page.status, page.unsynced) : 0),
  };
}

const ROOM_EVENTS = ['status', 'role', 'unsynced', 'reset', 'doc-ready', 'loaded'] as const;

/**
 * Opens a file for editing: the root document and the document holding its contents, each a room
 * on flobi-collab, each shown from this device's copy at once while it syncs. Also sets up an
 * undo history that only takes back this person's edits.
 */
export function useEditorDocument(documentId: string): DocumentSession {
  const { user } = useUser();
  const clerkId = user?.id ?? '';
  /** The users-back id (§21.47): presence, colours and "me" use it, never Clerk's. Rooms wait for it. */
  const [userId, setUserId] = useState('');
  const name = user?.firstName ?? user?.username ?? user?.primaryEmailAddress?.emailAddress ?? 'You';
  const avatarUrl = user?.imageUrl;
  const [session, setSession] = useState<SessionState>(FRESH);
  /** The tab's client, the root room and this person: they outlive the contents document. */
  const fileRef = useRef<{ collab: CollabClient; rootRoom: CollabRoom; me: Me; pageRooms: Set<CollabRoom> } | null>(null);
  const meRef = useRef<Me>({ id: userId, name, color: colorFor(userId || 'anonymous') });
  meRef.current = { id: userId, name, color: colorFor(userId || 'anonymous'), ...(avatarUrl ? { avatarUrl } : {}) };
  const [file, setFile] = useState(0);

  useEffect(() => {
    if (!clerkId) return;
    let live = true;
    setUserId('');
    myUserId(clerkId).then(
      (id) => live && setUserId(id),
      () => live && setSession((s) => ({ ...s, problem: 'This file can’t be opened right now. Try again in a moment.' })),
    );
    return () => {
      live = false;
    };
  }, [clerkId]);
  /** The documents the rooms hold now: a restore or a stale copy swaps them for fresh ones. */
  const [rootDoc, setRootDoc] = useState<Y.Doc | null>(null);
  const [pageRoom, setPageRoom] = useState<CollabRoom | null>(null);
  const [pageDoc, setPageDoc] = useState<Y.Doc | null>(null);

  // ── the file: one client, the root room ─────────────────────────────────
  useEffect(() => {
    if (!documentId || !userId) return;
    const me = meRef.current;
    const collab = createCollabClient(me);
    const rootName = rootRoomName(documentId);
    // §21.46: with no copy on this device, the stored file is the first content (and editable
    // through live.push while collab is away or switched off).
    const rootRoom = collab.open(rootName, { stored: () => storedState(rootName) });
    const held = { collab, rootRoom, me, pageRooms: new Set<CollabRoom>() };
    fileRef.current = held;
    setFile((count) => count + 1);

    const follow = () => setRootDoc(rootRoom.displayDoc);
    const offs: (() => void)[] = ROOM_EVENTS.map((event) => rootRoom.on(event, () => {
      follow();
      setSession((s) => ({ ...s, ...summaryOf(rootRoom, [...held.pageRooms][0] ?? null) }));
    }));
    // The server said no — a file that does not exist, is not in this workspace, was deleted
    // or taken away. Said now, in its own words, rather than fifteen seconds of "Opening the
    // file…" followed by a guess that the backend is down.
    let noContents: ReturnType<typeof setTimeout> | undefined;
    offs.push(
      rootRoom.on('refused', ({ reason }) => {
        clearTimeout(noContents);
        setSession((s) => ({ ...s, problem: refusalText(reason), stopped: s.open !== null || s.stopped, noAccess: needsAccess(reason) }));
      }),
    );
    // The kill switch has no server to start from: a file this device has never held cannot open.
    offs.push(
      rootRoom.on('loaded', ({ source }) => {
        if (source !== 'none' || rootRoom.live) return;
        clearTimeout(noContents);
        setSession((s) => ({ ...s, problem: refusalText(SWITCHED_OFF), stopped: true }));
      }),
    );
    follow();
    setSession((s) => ({ ...s, ...summaryOf(rootRoom, null) }));

    noContents = setTimeout(() => {
      setSession((s) => (s.pageId === null && !s.problem ? { ...s, problem: 'This file has not arrived — the backend may be down.' } : s));
    }, 15_000);

    return () => {
      clearTimeout(noContents);
      for (const off of offs) off();
      /**
       * The rooms close last: every one sends what it has first (what cannot go is kept on this
       * device and goes out the next time the file opens). Closing straight away cut off
       * whatever the contents still had to send — a nudge made just before All Files was gone.
       */
      closeWhenSent(collab, [rootRoom, ...held.pageRooms], DRAIN_MS);
      fileRef.current = null;
      setRootDoc(null);
      setPageRoom(null);
      setPageDoc(null);
      setSession(FRESH);
    };
  }, [documentId, userId]);

  // ── what the root document says: name, settings, and where the contents are ──
  useEffect(() => {
    if (!rootDoc) return;
    const readRoot = () => {
      const pages = listPages(rootDoc);
      const meta = readMeta(rootDoc);
      setSession((s) => ({
        ...s,
        // Before the server's copy arrives the root is empty and its name reads as the
        // "Untitled" placeholder, which the title showed on every open (QA 2026-09-24,
        // files-doc-F25). The backend makes the page with the file, so a page means the
        // root has arrived.
        name: pages.length > 0 ? meta.name : s.name,
        units: meta.units,
        resolution: meta.resolution,
        gridStep: meta.gridStep,
        gridDivisions: meta.gridDivisions,
        colorMode: meta.colorMode,
        // There is one, and the backend makes it with the file; this only waits for it
        // to arrive, and holds on to it once it has.
        pageId: s.pageId && pages.some((page) => page.id === s.pageId) ? s.pageId : (pages[0]?.id ?? null),
      }));
    };
    rootDoc.on('update', readRoot);
    readRoot();
    return () => rootDoc.off('update', readRoot);
  }, [rootDoc]);

  // ── the contents' room ───────────────────────────────────────────────────
  const pageId = session.pageId;
  useEffect(() => {
    const held = fileRef.current;
    if (!documentId || !pageId || !held) return;
    const pageName = pageRoomName(documentId, pageId);
    const room = held.collab.open(pageName, { stored: () => storedState(pageName) });
    held.pageRooms.add(room);
    const follow = () => setPageDoc(room.displayDoc);
    const offs: (() => void)[] = ROOM_EVENTS.map((event) => room.on(event, () => {
      follow();
      setSession((s) => ({ ...s, ...summaryOf(held.rootRoom, room) }));
    }));
    offs.push(
      room.on('refused', ({ reason }) => {
        setSession((s) => ({ ...s, problem: refusalText(reason), stopped: true, noAccess: needsAccess(reason) }));
      }),
    );
    setPageRoom(room);
    follow();
    setSession((s) => ({ ...s, ...summaryOf(held.rootRoom, room) }));
    return () => {
      for (const off of offs) off();
      setPageRoom(null);
      setPageDoc(null);
    };
  }, [documentId, pageId, file]);

  // ── the contents: their own scene and history ───────────────────────────
  useEffect(() => {
    const held = fileRef.current;
    if (!documentId || !pageId || !held || !rootDoc || !pageRoom || !pageDoc) return;
    const scene = new Scene(pageDoc);
    // One gesture, one step, one step per press (pageUndo).
    const undo = pageUndo(pageDoc, () => setSession((s) => ({ ...s, undoTakenOver: s.undoTakenOver + 1 })));
    // Undoing does not close the step it lands on: the next edit would join the step
    // BEFORE the one just undone, and one Cmd+Z later take back both. Close it.
    const closeAfterUndo = () => undo.stopCapturing();
    undo.on('stack-item-popped', closeAfterUndo);

    setSession((s) => ({
      ...s,
      open: { documentId, root: rootDoc, page: pageDoc, scene, undo, pageRoom, rootRoom: held.rootRoom, pageId, me: held.me },
      problem: s.stopped ? s.problem : null,
    }));

    return () => {
      undo.off('stack-item-popped', closeAfterUndo);
      undo.destroy();
      scene.dispose();
      setSession((s) => ({ ...s, open: null }));
    };
  }, [documentId, pageId, rootDoc, pageRoom, pageDoc, file]);

  /**
   * Leaving with edits the server has not confirmed asks first.
   *
   * They are kept on this device and go out the next time the file opens, which is why this is
   * the last line rather than the only one — a private window may refuse to keep them.
   */
  const unsent = session.pending > 0;
  useEffect(() => {
    if (!unsent) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Still required by some browsers to show the prompt at all.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsent]);

  return session;
}
